import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCalendarAlt,
  faUser,
  faClock,
  faTag,
  faShare,
  faHeart,
  faArrowRight,
  faQuoteLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faLinkedinIn,
  faXTwitter,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import { blogPosts } from "./Blogdata";
import SEO from "../components/SEO";

// ─── Animations ────────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideLeft = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.05); }
`;

// ─── Progress Bar ──────────────────────────────────────────────────────────────

const ReadingProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: ${(p) => p.$progress}%;
  height: 3px;
  background: linear-gradient(90deg, #990011, #a48d26, #990011);
  background-size: 200% auto;
  animation: ${shimmer} 2s linear infinite;
  z-index: 9999;
  transition: width 0.1s linear;
`;

// ─── Layout ────────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  background: ${(p) => p.theme.colors.background};
`;

// ─── Hero ──────────────────────────────────────────────────────────────────────

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  min-height: 600px;
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    height: 75vh;
  }
`;

const HeroImage = styled.div`
  position: absolute;
  inset: 0;
  background: url(${(p) => p.image}) center / cover no-repeat;
  transform: scale(1.05);
  animation: ${scaleIn} 1.2s ease-out forwards;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.92) 0%,
    rgba(0, 0, 0, 0.55) 45%,
    rgba(0, 0, 0, 0.2) 100%
  );
  z-index: 1;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
  animation: ${slideUp} 1s 0.3s ease-out both;

  @media (max-width: 768px) {
    padding: 0 1.25rem 2.5rem;
  }
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 0.5rem 1.1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 1.75rem;
  transition: all 0.3s ease;
  letter-spacing: 0.03em;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
    transform: translateX(-4px);
  }
`;

const CategoryPill = styled.span`
  display: inline-block;
  background: ${(p) => p.theme.colors.primary};
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.3rem 0.9rem;
  border-radius: 50px;
  margin-bottom: 1.25rem;
`;

const HeroTitle = styled.h1`
  font-size: clamp(1.8rem, 4.5vw, 3rem);
  font-weight: 800;
  color: white;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  font-family: Georgia, 'Times New Roman', serif;
  letter-spacing: -0.02em;
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;

  svg {
    color: ${(p) => p.theme.colors.secondary};
    font-size: 0.8rem;
  }
`;

// ─── Main Content ──────────────────────────────────────────────────────────────

const MainContent = styled.main`
  position: relative;
  z-index: 10;
  margin-top: -3rem;
  padding-bottom: 5rem;
`;

const ContentGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 2.5rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    padding: 0 1.25rem;
  }
`;

// ─── Article Card ──────────────────────────────────────────────────────────────

const ArticleCard = styled.article`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: ${slideUp} 0.8s 0.1s ease-out both;
`;

const ArticleBody = styled.div`
  padding: 3rem;

  @media (max-width: 768px) {
    padding: 1.75rem 1.25rem;
  }
`;

// ─── Body Parser Styles ────────────────────────────────────────────────────────

const BodyParagraph = styled.p`
  font-size: 1.05rem;
  line-height: 1.85;
  color: #444;
  margin-bottom: 1.6rem;
  font-family: Georgia, 'Times New Roman', serif;

  &:last-child { margin-bottom: 0; }
`;

const SectionHeading = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 2.25rem 0 0.9rem;
  padding-left: 1rem;
  border-left: 4px solid ${(p) => p.theme.colors.primary};
  line-height: 1.3;

  &:first-child { margin-top: 0; }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: linear-gradient(to right, ${(p) => p.theme.colors.primary}33, transparent);
  margin: 2.5rem 0;
`;

const HighlightBox = styled.blockquote`
  position: relative;
  background: linear-gradient(135deg, #990011 0%, #6b000c 100%);
  color: white;
  border-radius: 16px;
  padding: 2rem 2.25rem 2rem 3rem;
  margin: 2.5rem 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -30px;
    right: -30px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.07);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -40px;
    left: -20px;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
  }

  p {
    font-size: 1.05rem;
    line-height: 1.8;
    font-style: italic;
    font-family: Georgia, 'Times New Roman', serif;
    position: relative;
    z-index: 1;
    margin: 0;
  }

  svg {
    position: absolute;
    top: 1.5rem;
    left: 1.25rem;
    color: rgba(255, 255, 255, 0.25);
    font-size: 1.5rem;
    z-index: 1;
  }
`;

// ─── Tags ──────────────────────────────────────────────────────────────────────

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
`;

const Tag = styled.span`
  background: #f5f5f5;
  color: ${(p) => p.theme.colors.secondary};
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  border: 1px solid #e8e8e8;
`;

// ─── Author Strip ──────────────────────────────────────────────────────────────

const AuthorStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: #fafafa;
  border-top: 1px solid #eee;
  padding: 1.75rem 3rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const AuthorAvatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${(p) => p.theme.colors.primary}, ${(p) => p.theme.colors.secondary});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 800;
  font-size: 1.1rem;
  flex-shrink: 0;
  animation: ${pulse} 4s ease-in-out infinite;
`;

const AuthorInfo = styled.div`
  flex: 1;

  strong {
    display: block;
    font-size: 0.95rem;
    color: ${(p) => p.theme.colors.text};
    margin-bottom: 0.2rem;
  }

  span {
    font-size: 0.8rem;
    color: #888;
  }
`;

const HeartButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: 1.5px solid #eee;
  border-radius: 50px;
  padding: 0.45rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => (p.$liked ? p.theme.colors.primary : "#888")};
  border-color: ${(p) => (p.$liked ? p.theme.colors.primary : "#eee")};
  cursor: pointer;
  transition: all 0.25s ease;

  svg {
    color: ${(p) => (p.$liked ? p.theme.colors.primary : "#bbb")};
    font-size: 0.9rem;
    transition: transform 0.25s ease;
  }

  &:hover {
    border-color: ${(p) => p.theme.colors.primary};
    color: ${(p) => p.theme.colors.primary};

    svg { transform: scale(1.2); color: ${(p) => p.theme.colors.primary}; }
  }
`;

// ─── Share Section ─────────────────────────────────────────────────────────────

const ShareSection = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.07);
  padding: 1.75rem;
  margin-top: 1.75rem;
  animation: ${slideUp} 0.8s 0.25s ease-out both;
`;

const ShareTitle = styled.h3`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #aaa;
  margin-bottom: 1rem;
`;

const ShareButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ShareBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1.1rem;
  border-radius: 50px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.25s ease;
  border: none;

  ${(p) =>
    p.$fb &&
    css`
      background: #1877f2;
      color: white;
      &:hover { background: #1464d0; transform: translateY(-2px); }
    `}
  ${(p) =>
    p.$li &&
    css`
      background: #0a66c2;
      color: white;
      &:hover { background: #0855a0; transform: translateY(-2px); }
    `}
  ${(p) =>
    p.$tw &&
    css`
      background: #000;
      color: white;
      &:hover { background: #333; transform: translateY(-2px); }
    `}
  ${(p) =>
    p.$wa &&
    css`
      background: #25d366;
      color: white;
      &:hover { background: #1dba58; transform: translateY(-2px); }
    `}
`;

// ─── Sidebar ───────────────────────────────────────────────────────────────────

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  position: sticky;
  top: 6rem;
  animation: ${slideLeft} 0.8s 0.3s ease-out both;

  @media (max-width: 1024px) {
    position: static;
  }
`;

const SidebarCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.07);
  padding: 1.75rem;
  overflow: hidden;
`;

const SidebarTitle = styled.h3`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #aaa;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
`;

const TableOfContents = styled.nav``;

const TocItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.88rem;
  line-height: 1.4;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 2px solid transparent;

  &:hover {
    background: #fef5f6;
    color: ${(p) => p.theme.colors.primary};
    border-left-color: ${(p) => p.theme.colors.primary};
    padding-left: 1rem;
  }
`;

const RelatedPostCard = styled(Link)`
  display: flex;
  gap: 1rem;
  text-decoration: none;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f5f5f5;
  transition: all 0.2s ease;

  &:last-child { border-bottom: none; padding-bottom: 0; }
  &:first-child { padding-top: 0; }

  &:hover h4 { color: ${(p) => p.theme.colors.primary}; }
  &:hover img { transform: scale(1.05); }
`;

const RelatedThumb = styled.div`
  width: 72px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
`;

const RelatedInfo = styled.div`
  flex: 1;
  min-width: 0;

  h4 {
    font-size: 0.85rem;
    font-weight: 700;
    color: ${(p) => p.theme.colors.text};
    line-height: 1.35;
    margin-bottom: 0.3rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    transition: color 0.2s;
  }

  span {
    font-size: 0.75rem;
    color: #aaa;
  }
`;

// ─── Back to Blog banner ───────────────────────────────────────────────────────

const BackBanner = styled.div`
  max-width: 1200px;
  margin: 0 auto 2rem;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.25rem;
  }
`;

const BackBannerInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border-radius: 14px;
  padding: 1.1rem 1.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.6s ease-out both;
`;

const BackBannerLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(p) => p.theme.colors.text};
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover { color: ${(p) => p.theme.colors.primary}; }
`;

const ProgressLabel = styled.span`
  font-size: 0.8rem;
  color: #aaa;
  font-weight: 500;
`;

// ─── Body parser ──────────────────────────────────────────────────────────────

// Detects lines like "🏫 Section Title" as headings
const EMOJI_REGEX = /^(\p{Emoji_Presentation}|\p{Extended_Pictographic})\s+(.+)$/u;
const CONCLUSION_KEYWORDS = ["every action counts", "chaque action compte", "conviction", "cette mobilisation"];

function parseBody(body) {
  if (!body) return [];
  const blocks = [];
  const rawParagraphs = body.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);

  rawParagraphs.forEach((para, i) => {
    const lines = para.split("\n").map((l) => l.trim()).filter(Boolean);

    // Check if first line is an emoji heading
    const firstLine = lines[0] || "";
    const emojiMatch = firstLine.match(EMOJI_REGEX);

    if (emojiMatch) {
      blocks.push({ type: "heading", emoji: emojiMatch[1], text: emojiMatch[2] });
      const rest = lines.slice(1).join(" ").trim();
      if (rest) blocks.push({ type: "paragraph", text: rest });
    } else {
      // Check if this is a "conclusion" paragraph for highlight styling
      const lower = para.toLowerCase();
      const isHighlight = CONCLUSION_KEYWORDS.some((kw) => lower.includes(kw));

      if (isHighlight) {
        blocks.push({ type: "highlight", text: para });
      } else {
        blocks.push({ type: "paragraph", text: lines.join(" ") });
      }
    }
  });

  return blocks;
}

function extractTocItems(blocks) {
  return blocks
    .filter((b) => b.type === "heading")
    .map((b) => `${b.emoji} ${b.text}`);
}

// ─── Component ────────────────────────────────────────────────────────────────

const BlogDetailPage = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const [readingProgress, setReadingProgress] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const articleRef = useRef(null);

  const post = blogPosts.find((p) => p.id === parseInt(blogId));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [blogId]);

  useEffect(() => {
    const handleScroll = () => {
      const el = articleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const progress = Math.min(100, (scrolled / total) * 100);
      setReadingProgress(isNaN(progress) ? 0 : progress);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!post) return <Navigate to="/blog" />;

  const blocks = parseBody(post.body);
  const tocItems = extractTocItems(blocks);
  const related = blogPosts.filter((p) => p.id !== post.id).slice(0, 3);
  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(post.title);

  const handleLike = () => {
    setLiked(!liked);
    setLikeCount((c) => (liked ? c - 1 : c + 1));
  };

  const authorInitials = post.author
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <PageWrapper>
      <SEO
        title={`${post.title} | The Big Heart Association Blog`}
        description={post.excerpt}
        url={`/blog/${post.id}`}
        image={post.image ? `https://tbh.ma${post.image}` : undefined}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }, { name: post.title, url: `/blog/${post.id}` }]}
        article={{ author: post.author, datePublished: post.date, tags: post.tags }}
        noOrganizationSchema
      />
      <ReadingProgressBar $progress={readingProgress} />

      {/* ── Hero ── */}
      <HeroSection ref={articleRef}>
        <HeroImage image={post.image} />
        <HeroOverlay />
        <HeroContent>
          <BackBtn onClick={() => navigate("/blog")}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to Blog
          </BackBtn>
          <CategoryPill>
            <FontAwesomeIcon icon={faTag} style={{ marginRight: "0.4rem" }} />
            {post.category}
          </CategoryPill>
          <HeroTitle>{post.title}</HeroTitle>
          <HeroMeta>
            <MetaItem>
              <FontAwesomeIcon icon={faCalendarAlt} />
              {post.date}
            </MetaItem>
            <MetaItem>
              <FontAwesomeIcon icon={faUser} />
              {post.author}
            </MetaItem>
            <MetaItem>
              <FontAwesomeIcon icon={faClock} />
              {post.readTime}
            </MetaItem>
          </HeroMeta>
        </HeroContent>
      </HeroSection>

      {/* ── Body ── */}
      <MainContent>
        <BackBanner>
          <BackBannerInner>
            <BackBannerLink to="/blog">
              <FontAwesomeIcon icon={faArrowLeft} />
              All Articles
            </BackBannerLink>
            <ProgressLabel>{Math.round(readingProgress)}% read</ProgressLabel>
          </BackBannerInner>
        </BackBanner>

        <ContentGrid>
          {/* ── Article ── */}
          <div>
            <ArticleCard>
              <ArticleBody>
                {blocks.map((block, i) => {
                  if (block.type === "heading") {
                    return (
                      <SectionHeading key={i}>
                        {block.emoji} {block.text}
                      </SectionHeading>
                    );
                  }
                  if (block.type === "highlight") {
                    return (
                      <HighlightBox key={i}>
                        <FontAwesomeIcon icon={faQuoteLeft} />
                        <p>{block.text}</p>
                      </HighlightBox>
                    );
                  }
                  return <BodyParagraph key={i}>{block.text}</BodyParagraph>;
                })}

                {post.tags && (
                  <TagsRow>
                    {post.tags.map((tag, i) => (
                      <Tag key={i}>{tag}</Tag>
                    ))}
                  </TagsRow>
                )}
              </ArticleBody>

              <AuthorStrip>
                <AuthorAvatar>{authorInitials}</AuthorAvatar>
                <AuthorInfo>
                  <strong>{post.author}</strong>
                  <span>The Big Heart Association · {post.date}</span>
                </AuthorInfo>
                <HeartButton $liked={liked} onClick={handleLike}>
                  <FontAwesomeIcon icon={faHeart} />
                  {liked ? "Liked" : "Like"}
                  {likeCount > 0 && ` · ${likeCount}`}
                </HeartButton>
              </AuthorStrip>
            </ArticleCard>

            {/* ── Share ── */}
            <ShareSection>
              <ShareTitle>
                <FontAwesomeIcon icon={faShare} style={{ marginRight: "0.5rem" }} />
                Share this article
              </ShareTitle>
              <ShareButtons>
                <ShareBtn
                  $fb
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faFacebookF} />
                  Facebook
                </ShareBtn>
                <ShareBtn
                  $li
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareTitle}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faLinkedinIn} />
                  LinkedIn
                </ShareBtn>
                <ShareBtn
                  $tw
                  href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faXTwitter} />
                  X / Twitter
                </ShareBtn>
                <ShareBtn
                  $wa
                  href={`https://wa.me/?text=${shareTitle}%20${shareUrl}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faWhatsapp} />
                  WhatsApp
                </ShareBtn>
              </ShareButtons>
            </ShareSection>
          </div>

          {/* ── Sidebar ── */}
          <Sidebar>
            {tocItems.length > 0 && (
              <SidebarCard>
                <SidebarTitle>In this article</SidebarTitle>
                <TableOfContents>
                  {tocItems.map((item, i) => (
                    <TocItem key={i}>{item}</TocItem>
                  ))}
                </TableOfContents>
              </SidebarCard>
            )}

            {related.length > 0 && (
              <SidebarCard>
                <SidebarTitle>
                  <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: "0.4rem" }} />
                  More Articles
                </SidebarTitle>
                {related.map((rel) => (
                  <RelatedPostCard key={rel.id} to={`/blog/${rel.id}`}>
                    <RelatedThumb>
                      <img src={rel.image} alt={rel.title} />
                    </RelatedThumb>
                    <RelatedInfo>
                      <h4>{rel.title}</h4>
                      <span>{rel.date} · {rel.readTime}</span>
                    </RelatedInfo>
                  </RelatedPostCard>
                ))}
              </SidebarCard>
            )}

            <SidebarCard>
              <SidebarTitle>About TBH</SidebarTitle>
              <p style={{ fontSize: "0.88rem", color: "#666", lineHeight: 1.65, margin: 0 }}>
                The Big Heart Association is a Moroccan NGO founded in 2022, dedicated to improving
                education and living conditions for children and families in rural communities.
              </p>
              <Link
                to="/about"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  marginTop: "1rem",
                  color: "#990011",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  textDecoration: "none",
                }}
              >
                Learn more <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </SidebarCard>
          </Sidebar>
        </ContentGrid>
      </MainContent>
    </PageWrapper>
  );
};

export default BlogDetailPage;